import React from 'react';
import { CheckSquare, Square, ListChecks } from 'lucide-react';
import { useProgress } from '../../context/ProgressContext';
import { cn } from '../../lib/utils';

export function Checklist({ moduleId, items }) {
  const { progress, toggleChecklistItem } = useProgress();
  const moduleChecklist = progress.checklistItems[moduleId] || {};
  
  const completedCount = items.filter(item => moduleChecklist[item.id]).length;
  const allComplete = completedCount === items.length;

  return (
    <div 
      className="bg-white rounded-2xl border-2 border-stone-200 p-6 md:p-8"
      data-testid={`checklist-${moduleId}`}
    >
      <div className="flex items-start gap-4 mb-6">
        <div className={cn(
          "w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0",
          allComplete ? "bg-success/10" : "bg-secondary/20"
        )}>
          <ListChecks className={cn(
            "w-6 h-6",
            allComplete ? "text-success" : "text-secondary-700"
          )} />
        </div>
        <div>
          <h3 className="font-heading font-bold text-xl text-text mb-1">Your Checklist</h3>
          <p className="text-text-muted text-sm">
            {allComplete 
              ? "All tasks complete! Great work!" 
              : `${completedCount} of ${items.length} tasks done`
            }
          </p>
        </div>
      </div>

      <div className="space-y-3">
        {items.map((item) => {
          const isChecked = moduleChecklist[item.id];
          
          return (
            <button
              key={item.id}
              data-testid={`checklist-item-${item.id}`}
              onClick={() => toggleChecklistItem(moduleId, item.id)}
              className={cn(
                "w-full text-left p-4 rounded-xl border-2 transition-all checklist-item flex items-center gap-4",
                isChecked 
                  ? "completed border-success/30 bg-success/5" 
                  : "border-stone-200 hover:border-primary/50"
              )}
            >
              <div className={cn(
                "w-8 h-8 rounded-lg flex items-center justify-center transition-colors",
                isChecked ? "bg-success text-white" : "bg-stone-100 text-stone-400"
              )}>
                {isChecked ? (
                  <CheckSquare className="w-5 h-5" />
                ) : (
                  <Square className="w-5 h-5" />
                )}
              </div>
              <span className={cn(
                "font-medium text-text transition-all",
                isChecked && "line-through text-text-muted"
              )}>
                {item.label}
              </span>
            </button>
          );
        })}
      </div>

      {/* Progress indicator */}
      <div className="mt-6 pt-4 border-t border-stone-100">
        <div className="flex justify-between text-sm text-text-muted mb-2">
          <span>Progress</span>
          <span>{Math.round((completedCount / items.length) * 100)}%</span>
        </div>
        <div className="h-3 bg-stone-100 rounded-full overflow-hidden">
          <div 
            className={cn(
              "h-full rounded-full transition-all duration-500",
              allComplete ? "bg-success" : "bg-primary"
            )}
            style={{ width: `${(completedCount / items.length) * 100}%` }}
          />
        </div>
      </div>
    </div>
  );
}
